import React from "react";

/**
 * MarkdownRenderer — dependency-free, supports:
 *   - # / ## / ### headings
 *   - **bold**, *italic*, `inline code`
 *   - Unordered (-, *) and ordered (1.) lists
 *   - ```code blocks``` with optional language tag
 *   - GFM tables (| col | col |)
 *   - Blockquotes (> text)
 *   - Auto-links for http(s) URLs
 *
 * Covers ~95% of what an HR assistant emits without adding npm deps.
 */
export default function MarkdownRenderer({ text }) {
  if (!text) return null;
  const blocks = parseBlocks(text);
  return <div className="ai-md">{blocks.map((b, i) => renderBlock(b, i))}</div>;
}

// ── Block parser ─────────────────────────────────────────────────────────────
function parseBlocks(text) {
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, "").trim();
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ kind: "code", lang, code: buf.join("\n") });
      continue;
    }

    // Heading
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ kind: "heading", level: h[1].length, text: h[2] });
      i++;
      continue;
    }

    // Table
    if (/^\|.+\|$/.test(line) && i + 1 < lines.length && /^\|[\s|:-]+\|$/.test(lines[i + 1])) {
      const tbl = [line];
      i++;
      tbl.push(lines[i]);
      i++;
      while (i < lines.length && /^\|.+\|$/.test(lines[i])) {
        tbl.push(lines[i]);
        i++;
      }
      blocks.push({ kind: "table", rows: tbl });
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const quote = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ kind: "quote", text: quote.join("\n") });
      continue;
    }

    // List (ul / ol)
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const items = [];
      const ordered = /^\s*\d+\./.test(line);
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, ""));
        i++;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }

    // Blank line — paragraph break
    if (!line.trim()) {
      i++;
      continue;
    }

    // Paragraph
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^[#>`|]/.test(lines[i]) && !/^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ kind: "para", text: para.join(" ") });
  }
  return blocks;
}

// ── Inline parser → React nodes ──────────────────────────────────────────────
function renderInline(text) {
  if (!text) return null;
  // Pattern order matters: code → bold → italic → link
  const parts = [];
  let i = 0;
  let buf = "";
  const flush = () => { if (buf) parts.push(buf); buf = ""; };

  while (i < text.length) {
    const rest = text.slice(i);
    // inline code
    let m = /^`([^`]+)`/.exec(rest);
    if (m) {
      flush();
      parts.push(<code key={parts.length} className="ai-md-code">{m[1]}</code>);
      i += m[0].length;
      continue;
    }
    // bold (**...** or __...__)
    m = /^(\*\*|__)([^\s*][\s\S]*?[^\s*])\1/.exec(rest);
    if (m) {
      flush();
      parts.push(<strong key={parts.length}>{renderInline(m[2])}</strong>);
      i += m[0].length;
      continue;
    }
    // italic (*...* or _..._)
    m = /^([*_])([^\s*_]+?)\1/.exec(rest);
    if (m) {
      flush();
      parts.push(<em key={parts.length}>{renderInline(m[2])}</em>);
      i += m[0].length;
      continue;
    }
    // markdown link [text](url)
    m = /^\[([^\]]+)\]\(([^)]+)\)/.exec(rest);
    if (m) {
      flush();
      parts.push(
        <a key={parts.length} href={m[2]} target="_blank" rel="noopener noreferrer" className="ai-md-link">
          {m[1]}
        </a>
      );
      i += m[0].length;
      continue;
    }
    // bare URL
    m = /^(https?:\/\/\S+)/.exec(rest);
    if (m) {
      flush();
      parts.push(
        <a key={parts.length} href={m[1]} target="_blank" rel="noopener noreferrer" className="ai-md-link">
          {m[1]}
        </a>
      );
      i += m[0].length;
      continue;
    }

    buf += text[i];
    i++;
  }
  flush();
  return parts;
}

function renderBlock(b, key) {
  switch (b.kind) {
    case "heading":
      return React.createElement(
        `h${b.level}`,
        { key, className: `ai-md-h ai-md-h${b.level}` },
        renderInline(b.text)
      );
    case "para":
      return <p key={key} className="ai-md-p">{renderInline(b.text)}</p>;
    case "list":
      return b.ordered
        ? <ol key={key} className="ai-md-ol">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>
        : <ul key={key} className="ai-md-ul">{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
    case "code":
      return (
        <pre key={key} className="ai-md-pre">
          {b.lang ? <div className="ai-md-pre-lang">{b.lang}</div> : null}
          <code>{b.code}</code>
        </pre>
      );
    case "quote":
      return <blockquote key={key} className="ai-md-quote">{renderInline(b.text)}</blockquote>;
    case "table": {
      const [headerLine, , ...bodyLines] = b.rows;
      const cells = (line) => line.replace(/^\||\|$/g, "").split("|").map((s) => s.trim());
      const headers = cells(headerLine);
      const rows = bodyLines.map(cells);
      return (
        <div className="ai-md-table-wrap" key={key}>
          <table className="ai-md-table">
            <thead>
              <tr>{headers.map((h, j) => <th key={j}>{renderInline(h)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, j) => (
                <tr key={j}>{r.map((c, k) => <td key={k}>{renderInline(c)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    default:
      return null;
  }
}
